<template>
    <dx-popup
        v-model:visible="locState.userChangePasswordPopupVisible"
        :show-close-button="true"
        :show-title="true"
        width="560px"
        height="auto"
        min-height="750px"
        :animation="{ value: false }"
        @shown="onShown"
        @hidden="onHidden"
    >
        <generated-password-popup @usePassword="setGeneratedPassword" />

        <div class="px-4 py-2 h-full flex flex-col items-center text-[#031425]">
            <div class="text-4xl font-medium">Смена пароля</div>
            <div class="mt-4 w-full h-full flex flex-col justify-between">
                <div class="h-[90%] flex flex-col justify-between">
                    <div>
                        <div class="flex flex-col gap-4" @copy="onCopy">
                            <dx-text-box
                                class="custom-textbox"
                                v-bind="oldPasswordProps"
                                v-model:value="passwords.old.password"
                            />
                            <dx-text-box
                                class="custom-textbox"
                                v-bind="newPasswordProps"
                                v-model:value="passwords.new.password"
                            />
                            <dx-text-box
                                class="custom-textbox"
                                v-bind="newRepeatPasswordProps"
                                v-model:value="passwords.newRepeat.password"
                            />
                        </div>

                        <div class="mt-3 w-[90%] h-[60%]">
                            <div>
                                <label class="text-sm font-bold">{{ labelPassword }}</label>
                                <ul class="mt-1 ml-5 py-2 text-sm list-disc">
                                    <li v-for="(rule, index) in locState.userRequestBody.base" :key="index">
                                        {{ rule }}
                                    </li>
                                </ul>
                            </div>
                            <div v-if="isFirstTry">
                                <label class="mt-4 text-sm font-bold">Доп. требования:</label>
                                <ul class="mt-1 ml-5 py-2 text-sm list-disc">
                                    <li v-for="(rule, index) in locState.userRequestBody.optional" :key="index">
                                        {{ rule }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col">
                    <ai-button
                        height="45px"
                        text="Сменить пароль"
                        @click="changePassword"
                        :disabled="!isButtonDisabled"
                    />
                </div>
            </div>
        </div>
    </dx-popup>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { DxPopup, DxTextBox } from 'devextreme-vue';
import GeneratedPasswordPopup from '@/views/administration/users/generated-password-popup.vue';
import AiButton from '@/components/ai-button/ai-button.vue';
import usersLocalStore from '@/views/administration/users/store.js';
import enviromentData from '@/store/enviroment-data.js';
import { showLoadPanel, hideLoadPanel } from '@/components/utils/ai-global-load-panel.js';
import { dxUiNotify } from '@/utils/dx-helper.js';
import { checkPasswordValidate, putNewPassword } from '@/service/admin-service.js';

const { state: envState } = enviromentData;
const { state: locState, getPasswordValidateRulesList } = usersLocalStore;

const labelPassword = ref('Пароль Вашей учетной записи должен соответствовать следующим требованиям:');
const isFirstTry = ref(true);

const isButtonDisabled = computed(
    () =>
        passwords.new.password === passwords.newRepeat.password &&
        passwords.old.password !== '' &&
        passwords.new.password !== '',
);

const passwords = reactive({
    old: {
        password: '',
        display: false,
        validStatus: true,
    },
    new: {
        password: '',
        display: false,
        validStatus: true,
    },
    newRepeat: {
        password: '',
        display: false,
        validStatus: true,
    },
});

const oldPasswordProps = computed(() => ({
    placeholder: 'Введите текущий пароль',
    inputAttr: {
        style: passwords.old.display ? '-webkit-text-security: none' : '-webkit-text-security: disc',
    },
    isValid: passwords.old.validStatus,
    validationError: { message: 'Текущий пароль введен неверно' },
    validationMessageMode: 'always',
    valueChangeEvent: 'input keyup change paste',
    buttons: [
        {
            location: 'after',
            name: 'changePasswordVisibilityButton',
            options: {
                icon: passwords.old.display
                    ? '../../../assets/password-visibility-off.svg'
                    : '../../../assets/password-visibility-on.svg',
                onClick: () => (passwords.old.display = !passwords.old.display),
            },
        },
    ],
    onInput: () => passwords.old.validStatus = true,
    onPaste: () => passwords.old.validStatus = true,
}));

const newPasswordProps = computed(() => ({
    placeholder: 'Введите новый пароль',
    inputAttr: {
        style: passwords.new.display ? '-webkit-text-security: none' : '-webkit-text-security: disc',
    },
    isValid: passwords.new.validStatus,
    validationError: { message: 'Новый пароль не соответствует требованиям' },
    validationMessageMode: 'always',
    valueChangeEvent: 'input keyup change paste',
    buttons: [
        {
            location: 'after',
            name: 'showGeneratedPassword',
            options: {
                icon: '../../../assets/password-generate.svg',
                onClick: () => showGeneratedPassword(),
            },
        },
        {
            location: 'after',
            name: 'changePasswordVisibilityButton',
            options: {
                icon: passwords.new.display
                    ? '../../../assets/password-visibility-off.svg'
                    : '../../../assets/password-visibility-on.svg',
                onClick: () => (passwords.new.display = !passwords.new.display),
            },
        },
    ],
    onInput: () => passwords.new.validStatus = true,
    onPaste: () => passwords.new.validStatus = true,
}));

const newRepeatPasswordProps = computed(() => ({
    inputAttr: {
        style: passwords.newRepeat.display ? '-webkit-text-security: none' : '-webkit-text-security: disc',
    },
    placeholder: 'Повторите новый пароль',
    isValid: passwords.newRepeat.validStatus,
    validationError: { message: 'Новый пароль и пароль подтверждения не совпадают' },
    valueChangeEvent: 'input keyup change paste',
    buttons: [
        {
            location: 'after',
            name: 'changePasswordVisibilityButton',
            options: {
                icon: passwords.newRepeat.display
                    ? '../../../assets/password-visibility-off.svg'
                    : '../../../assets/password-visibility-on.svg',
                onClick: () => (passwords.newRepeat.display = !passwords.newRepeat.display),
            },
        },
    ],
    onFocusOut: () => {
        passwords.newRepeat.validStatus = doPasswordsMatch();
    },
    onInput: () => {
        if (passwords.new.password && passwords.newRepeat.password) {
            passwords.newRepeat.validStatus = doPasswordsMatch();
        }
    },
}));

const doPasswordsMatch = () => passwords.newRepeat.password === passwords.new.password;

const onCopy = (event) => {
    event.preventDefault();
    event.clipboardData.setData('text/plain', event.target.value);
};

const onShown = async () => {
    showLoadPanel();
    await getPasswordValidateRulesList();
    locState.userRequestBody.base = locState.userRequestBody.password_rules.base;
    locState.userRequestBody.optional = locState.userRequestBody.password_rules.optional;
    hideLoadPanel();
};

const onHidden = (event) => {
    if (event.event?.key === 'Escape') {
        event.cancel = true;
        return;
    }
    reset();
    locState.userChangePasswordPopupVisible = false;
};

const showGeneratedPassword = () => {
    locState.generatedPasswordPopupVisible = true;
};

const reset = () => {
    labelPassword.value = 'Пароль Вашей учетной записи должен соответствовать следующим требованиям:';
    isFirstTry.value = true;
    passwords.old = {
        password: '',
        display: false,
        validStatus: true,
    };
    passwords.new = {
        password: '',
        display: false,
        validStatus: true,
    };
    passwords.newRepeat = {
        password: '',
        display: false,
        validStatus: true,
    };
};

const setGeneratedPassword = (generatedPassword) => {
    passwords.new.password = generatedPassword;
    passwords.new.validStatus = true;
};

const changePassword = async () => {
    showLoadPanel();
    isFirstTry.value = false;
    passwords.old.validStatus = true;
    passwords.new.validStatus = true;
    passwords.newRepeat.validStatus = true;

    const resultValidate = await checkPasswordValidate(locState.userRequestBody.user_type_id, passwords.new.password);
    let resultOldNewPasswords = {};

    if (!resultValidate.success) {
        passwords.new.validStatus = false;
        labelPassword.value = 'Слeдующие требования к паролю не учтены:';
        locState.userRequestBody.base = resultValidate.errors?.map(el => el.message);
        locState.userRequestBody.optional = [];
    } else {
        labelPassword.value = 'Новый пароль соответствует всем требованиям';
        locState.userRequestBody.base = [];
        locState.userRequestBody.optional = [];
        resultOldNewPasswords = await putNewPassword(
            envState.userData.id,
            passwords.old.password,
            passwords.new.password,
        );
        passwords.old.validStatus = !!resultOldNewPasswords.success;
        if (resultOldNewPasswords.success) {
            locState.userChangePasswordPopupVisible = false;
            dxUiNotify('Пароль обновлен', 'success');
        }
    }
    hideLoadPanel();
};
</script>

<style scoped>
.custom-textbox {
    background: white;
    border-radius: 5px;
    border: 1px solid #03142533;
    color: black;
}

.custom-textbox:hover {
    background: white;
}
</style>
